import { useEffect, type CSSProperties } from "react";
import { useAutoScroll } from "@/hooks/ui/useAutoScroll";
import { ArrowDown, Volume2, VolumeX, X } from "lucide-react";
import { TextMessageInput } from "@/components/VoiceAgent";
import type { GenieAttachment } from "@/types/voice/genie-attachment";
import { useMediaLookupPending } from "@/hooks/voice/useMediaLookupPending";
import { GenieAttachmentCard, GenieAttachmentPending } from "./GenieAttachmentCard";

export interface TranscriptMessage {
  message: string;
  isUser: boolean;
  timestamp: Date;
  imageAttachments?: Array<{
    id: string;
    previewUrl: string;
    analysisText?: string;
  }>;
  /** A link/picture the Genie put on screen (showRelatedMedia); such entries carry an empty message. */
  attachment?: GenieAttachment;
}

interface TranscriptPanelProps {
  open: boolean;
  brandColor: string;
  agentName?: string;
  messages: TranscriptMessage[];
  onClose: () => void;
  width?: number;
  maxHeightVh?: number;
  insetRightVw?: number; // how far to pull away from the right edge (desktop spacing)
  isConnected?: boolean;
  isListening?: boolean;
  onSendMessage?: (text: string) => void;
  isMuted?: boolean;
  onToggleMute?: () => void;
}

export const TranscriptPanel = ({
  open,
  brandColor,
  agentName,
  messages,
  onClose,
  width = 420,
  maxHeightVh = 70,
  insetRightVw = 0,
  isConnected = false,
  isListening = false,
  onSendMessage,
  isMuted = false,
  onToggleMute,
}: TranscriptPanelProps) => {
  const mediaPending = useMediaLookupPending();
  const { scrollRef, bottomRef, scrollToBottom, isAtBottom } = useAutoScroll({
    // On iOS, a "smooth" programmatic scroll can visually fight with user's manual scrolling.
    // We only want smooth follow while the conversation is active.
    smooth: isConnected,
    // Keep tolerance small so iOS doesn't consider "almost at bottom" as "at bottom".
    threshold: 10,
    // After conversation ends we don't want the UI to fight the user's manual scrolling.
    autoScrollOnContentChange: isConnected && messages.length > 0,
  });

  // When transcript is opened after conversation end, show the latest messages once,
  // then keep auto-follow disabled so the user can scroll history.
  useEffect(() => {
    if (!open || messages.length === 0) return;
    if (!isConnected) return;
    scrollToBottom();
    // Intentionally do NOT depend on `messages.length`:
    // after disconnect, transcripts can still append final chunks and we don't
    // want to auto-jump while the user is trying to read history.
  }, [open, isConnected, scrollToBottom]);

  if (!open) return null;

  return (
    <aside
      style={{
        // This panel is used both in desktop row layouts and inside
        // mobile vertical flex containers. Avoid using `width` as a flex-basis
        // (it would accidentally become height in column layout).
        flex: "1 1 auto",
        alignSelf: "stretch",
        width: "100%",
        maxWidth: `${width}px`,
        minHeight: 0,
        maxHeight: `${maxHeightVh}vh`,
        marginRight: insetRightVw ? `${insetRightVw}vw` : 0,
        marginLeft: 0,
        background: "transparent",
        border: "none",
        borderRadius: 12,
        padding: 4,
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 8, gap: 8 }}>
        {isConnected && onToggleMute && (
          <button
            onClick={onToggleMute}
            style={{
              background: isMuted ? `${brandColor}15` : "transparent",
              border: isMuted ? `1px solid ${brandColor}` : "1px solid #E5E7EB",
              borderRadius: 8,
              padding: "6px 10px",
              color: isMuted ? brandColor : "#6B7280",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: "0.8rem",
              fontWeight: 500,
              transition: "all 0.2s ease",
            }}
            aria-label={isMuted ? "Unmute conversation" : "Mute conversation"}
            title={isMuted ? "Click to unmute (enable voice & mic)" : "Click to mute (text-only mode)"}
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            {isMuted ? "Unmute" : "Mute"}
          </button>
        )}
        <button
          onClick={onClose}
          style={{ background: "transparent", border: "none", color: "#6B7280", cursor: "pointer" }}
          aria-label="Close transcript"
        >
          <X size={16} />
        </button>
      </div>
      <div
        ref={scrollRef}
        className="brand-scroll"
        style={{
          overflowY: "auto",
          paddingRight: 4,
          paddingBottom: 12,
          position: "relative",
          flex: 1,
          minHeight: 0,
          "--brand-color": brandColor,
        } as CSSProperties}
      >
        {messages.length === 0 ? (
          <div style={{ color: brandColor + "AA", textAlign: "center", padding: 20 }}>Start talking to see the transcript</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {messages.map((msg, i) =>
              msg.attachment ? (
                <GenieAttachmentCard key={i} attachment={msg.attachment} brandColor={brandColor} />
              ) : (
                <div
                  key={i}
                  style={{
                    padding: 10,
                    borderRadius: 10,
                    background: msg.isUser ? "#FFFFFFE6" : brandColor,
                    color: msg.isUser ? "#111827" : "#FFFFFF",
                    boxShadow: msg.isUser ? "0 4px 14px rgba(0,0,0,0.06)" : "0 6px 16px rgba(0,0,0,0.10)",
                    border: msg.isUser ? "1px solid #E5E7EB" : "none",
                    maxWidth: "80%",
                    alignSelf: msg.isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <div style={{ fontSize: "0.75rem", color: msg.isUser ? "#6B7280" : "#E0ECFF", marginBottom: 4, textAlign: msg.isUser ? "right" : "left" }}>
                    {msg.isUser ? "You" : agentName || "Genie"} • {msg.timestamp.toLocaleTimeString()}
                  </div>
                  <div style={{ fontSize: "0.92rem", lineHeight: 1.45, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                    {msg.message}
                  </div>
                </div>
              ),
            )}
            {mediaPending && <GenieAttachmentPending brandColor={brandColor} />}
            <div ref={bottomRef} style={{ height: 1 }} />
          </div>
        )}

        {/* Scroll to bottom button */}
        {!isAtBottom && messages.length > 0 && (
          <button
            onClick={scrollToBottom}
            style={{
              position: "sticky",
              bottom: 12,
              left: "50%",
              transform: "translateX(-50%)",
              background: brandColor,
              color: "#FFFFFF",
              border: "none",
              borderRadius: 20,
              padding: "8px 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: "0.85rem",
              fontWeight: 500,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 10,
            }}
            aria-label="Scroll to bottom"
          >
            New message
            <ArrowDown size={14} />
          </button>
        )}
      </div>

      {onSendMessage && (
        <div style={{ marginTop: 12, width: "100%" }}>
          <TextMessageInput
            onSendMessage={onSendMessage}
            isConnected={isConnected}
            isListening={isListening}
            brandColor={brandColor}
            placeholder="Type a message..."
          />
        </div>
      )}
    </aside>
  );
};

export default TranscriptPanel;


